import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { Permissions } from '@app/core/guard/Permissions';
import { PartnerDealCommitteeQueueItemResponse } from '@interfaces/partners/responses/partner-deal-committee.response';
import { DateFormatterService } from '@services/helpers/date-formatter.service';
import { PermissionsService } from '@services/helpers/permissions.service';
import { PartnerDealCommitteeHttpService } from '@services/http/partners/partner-deal-committee-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { MenuItem } from 'primeng/api';
import { forkJoin, interval, map } from 'rxjs';
import { CommitteeVoterBadgesComponent } from '@app/features/dashboard/commercial/pages/credit-committee/committee-voter-badges/committee-voter-badges.component';
import {
  approvedShare,
  computeQueueKpis,
  describeDeadline,
  describeQueueSituation,
  describeScore,
  splitOpenQueue,
} from '@app/features/dashboard/commercial/pages/credit-committee/credit-committee-presenter';

/**
 * Fila do comitê de crédito (card 3789), em Comercial › Comitê de crédito. Desenho "Prioridade primeiro"
 * (opção A do protótipo, aprovada em 04/11/2026): no topo, um cartão por deal que espera o voto de quem
 * está vendo; embaixo, a tabela do que está em votação com os outros; no fim, os decididos nos últimos
 * 30 dias, que abrem com `?view=decided`. Cartão e linha abrem o deal na etapa Comitê
 * (`?stage=committee`), onde se vota.
 *
 * Só o time interno (`PARTNERDEALCOMMITTEE_VIEW`). Tela de leitura: as contagens e a situação de
 * cada linha vêm prontas do servidor; o `credit-committee-presenter` só as transforma em texto.
 */
@Component({
  selector: 'app-credit-committee-queue',
  standalone: true,
  imports: [CommonModule, RouterLink, SharedModule, CommitteeVoterBadgesComponent],
  templateUrl: './credit-committee-queue.component.html',
  styleUrl: './credit-committee-queue.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreditCommitteeQueueComponent implements OnInit {
  private readonly committeeHttp = inject(PartnerDealCommitteeHttpService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dates = inject(DateFormatterService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);

  protected readonly breadcrumbs: MenuItem[] = [
    { label: 'Comercial' },
    { label: 'Comitê de crédito' },
  ];
  protected readonly home: MenuItem = {
    icon: 'pi pi-home',
    routerLink: '/dashboard/dashboard-management',
  };

  protected readonly canConfigure = toSignal(
    inject(PermissionsService).hasPermission(Permissions.PartnerDealCommittee.CONFIGURE),
    { initialValue: false }
  );

  protected readonly openItems = signal<PartnerDealCommitteeQueueItemResponse[]>([]);
  protected readonly decidedItems = signal<PartnerDealCommitteeQueueItemResponse[]>([]);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);

  /** Agora, em ms, renovado a cada minuto para o "vence em" andar sozinho (relógio de tela, não busca dado). */
  protected readonly now = toSignal(
    interval(60_000).pipe(map(() => this.dates.getNowTimestamp())),
    { initialValue: this.dates.getNowTimestamp() }
  );

  /** Os decididos aparecem abertos quando a URL tem `?view=decided` (dá para mandar o link). */
  protected readonly showDecided = toSignal(
    this.route.queryParamMap.pipe(map(params => params.get('view') === 'decided')),
    { initialValue: false }
  );

  protected readonly kpis = computed(() => computeQueueKpis(this.openItems(), this.decidedItems()));
  protected readonly split = computed(() => splitOpenQueue(this.openItems()));
  /** Contestações e prazos vencidos só da tabela "com os outros" (os cartões mostram os seus). */
  protected readonly othersContests = computed(() =>
    this.split().others.reduce((sum, i) => sum + (i.openContestCount ?? 0), 0)
  );
  protected readonly othersOverdue = computed(
    () => this.split().others.filter(i => i.isOverdue).length
  );
  protected readonly approvedPercent = computed(() =>
    approvedShare(this.kpis().approved, this.kpis().decided)
  );

  protected readonly situation = describeQueueSituation;
  protected readonly score = describeScore;

  ngOnInit(): void {
    this.load();
  }

  /** Lê as duas listas: em comitê e decididos nos últimos 30 dias. */
  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    forkJoin({
      open: this.committeeHttp.getQueue('open'),
      decided: this.committeeHttp.getQueue('decided'),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ open, decided }) => {
          this.openItems.set(open);
          this.decidedItems.set(decided);
          this.loading.set(false);
        },
        error: err => {
          this.loading.set(false);
          this.error.set(
            err?.errors?.[0]?.message ??
              'Não foi possível carregar a fila do comitê agora. Clique em Tentar de novo.'
          );
        },
      });
  }

  /** Abre ou fecha os decididos, guardando a escolha na URL. */
  protected toggleDecided(): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { view: this.showDecided() ? null : 'decided' },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  /** Data e hora sem segundos, no fuso e no formato do perfil ("05/11/2026 11:40"). */
  protected dateTime(value: string | null): string {
    return this.dates.formatDateTimeMinutesInstant(value);
  }

  /** "vence em 1 dia e 4 h" / "venceu há 2 h", pelo relógio da tela. */
  protected deadline(item: PartnerDealCommitteeQueueItemResponse): string {
    const deadline = this.dates.parseDate(item.deadlineAt)?.getTime();
    return deadline == null ? '' : describeDeadline(deadline, this.now()).label;
  }

  /** Rota do deal na etapa Comitê. */
  protected dealRoute(item: PartnerDealCommitteeQueueItemResponse): string[] {
    return ['/dashboard/commercial/my-deals', item.dealId];
  }
}
